import {
  getScore,
  getWeights,
  isValidInput,
  sortSpecs4AllTools,
  sortSpecsByWeight
} from 'msp-scoring';
import { Component } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';

import { Markdown } from '@client:common/Markdown';
import { Model } from '@client:common/Model';
import { ConfirmationDialog } from '@client:common/overlay/dialog';
import { Notification } from '@client:common/overlay/notification';
import {
  Template,
  TemplateLayer,
  TemplateLayerChildren
} from '@client:common/Template';
import { Tool } from '@client:common/Tool';
import { Drawer } from '@client:common/drawer';

import { Deque } from './models/Deque';
import { MarkerPage, Option, Page } from './models/FlowChart';
import { Ranking } from './models/Ranking';
import { SelectionWizardService } from './services/SelectionWizardService';
import { CurrentRanking } from './views/current-ranking/CurrentRanking';
import { EvaluationResult } from './views/evaluation-result/EvaluationResult';
import { Glossary } from './views/glossary/Glossary';
import { InvalidInputIndicator } from './views/invalid-input-indicator/InvalidInputIndicator';
import { TransitionTrail } from './views/transition-trail/TransitionTrail';
import { UserInputCollector } from './views/user-input-collector/UserInputCollector';
import { WizardControl } from './views/wizard-control/WizardControl';

import './SelectionWizard.light.scss';
import './SelectionWizard.dark.scss';

interface Props {
  models: Model[];
  setModels: (models: any) => void;
  setTools: (tools: any) => void;
  // setTemplate: (template: any) => void;
  template: Template;
  tools: {
    [toolName: string]: Tool;
  };
  loadSession: any;
  isMobile: boolean;
}

interface State {
  previousPage: Page;
  currentPage: Page;
  currentMobilePage: Page;
  showNothing: boolean;
  nextPage: Page;
  visitedPageTrail: Page[];
  rankings: Ranking[];
  optionToShowGlossary: Option;
  isInputValid: boolean;
  complete: boolean;
  isExisting: boolean;
  sessionName: string;
  ULID: string;
  sortedSpecsByWeight: Array<{
    path: string;
    weight: number;
  }>;
  template: Template;
  tools: {
    [toolName: string]: Tool;
  };
  openRightDrawer: boolean;
  showGlossaryPopup: boolean;
}

/*
new/**
 * Takes an object reference and turns it into a unique id string.
 * @param {*} object
 * @returns {string}
export function getIDOfObject(object) {
    const key = storedReferencesToObjects.get(object);
    if (key) {
        return key;
    }
    /* current millisecond and a random number. Will most likely not cause collisions.
        Could use uuid but didn't see a need for this case. This will only be used over
        the life of the loaded page in the browser.
    const newKey = `objectKey(${new Date().getTime()}${Math.random()})`;
    storedReferencesToObjects.set(object, newKey);
    return newKey;
}

// entry a is an object key.
// instantiate object, then use key as the key and the object as the value in a lookup map.
//do the same for all other entires, but if the key is in the lookup map, just use the string key for the lookup to get the object key



*/

export class SelectionWizard extends Component<Props, State> {
  private answerTracker = new Map<Page, Option[]>();

  private _pagesToVisit = new Deque<Page>();
  private readonly _selectionWizardService =
    SelectionWizardService.getInstance();

  private _flowChartSequence: Readonly<Page[]>;
  private _pageDefinitionMap: Map<string, Page>;
  private _uuidLookup: Map<string, Page | Option>;

  constructor(props: Props) {
    super(props);

    this.state = {
      previousPage: null,
      currentPage: null,
      showNothing: null,
      nextPage: null,
      visitedPageTrail: [],
      rankings: [],
      optionToShowGlossary: null,
      isInputValid: true,
      sortedSpecsByWeight: [],
      template: null,
      tools: null,
      complete: false,
      isExisting: false,
      ULID: null,
      sessionName: null,
      openRightDrawer: false,
      currentMobilePage: null,
      showGlossaryPopup: false
    };

    this.onMakePageCurrent = this.onMakePageCurrent.bind(this);
    this.onAnswerChange = this.onAnswerChange.bind(this);
    this.onShowGlossary = this.onShowGlossary.bind(this);
    this.onStartOver = this.onStartOver.bind(this);
    this.onSaveSession = this.onSaveSession.bind(this);
  }

  componentDidMount() {
    const { loadSession, template } = this.props;
    this._selectionWizardService.getFlowChart().then((flowChart) => {
      flowChart.sequence.push(MarkerPage.EXIT);
      this._pageDefinitionMap = flowChart.pageDefinitionMap;
      this._uuidLookup = new Map();
      for (const [, definitionItem] of this._pageDefinitionMap) {
        this._uuidLookup.set(definitionItem.UUID, definitionItem);
        for (const option of definitionItem.options) {
          this._uuidLookup.set(option.UUID, option);
        }
      }

      this._uuidLookup.set(MarkerPage.EXIT.UUID, MarkerPage.EXIT);

      this._flowChartSequence = Object.freeze(flowChart.sequence);
      if (!loadSession) {
        this.setState({
          currentPage: this._flowChartSequence[0],
          visitedPageTrail: [...flowChart.sequence]
        });
      }
      /* Load existing session */
      if (loadSession) {
        const updatedSession = { ...loadSession };
        const {
          nextPage: nextPageUUID,
          currentPage: currentPageUUID,
          visitedPageTrail: visitedPageTrailUUIDArray,
          sortedSpecsByWeight,
          tools,
          complete
        } = updatedSession.session.state;
        const { flowChartSequence, answerTracker } =
          updatedSession.session.internal;

        this.answerTracker = new Map();
        for (const [pageUUID, optionsUUIDArray] of answerTracker) {
          const options = optionsUUIDArray.map((optionUUID: string) =>
            this._uuidLookup.get(optionUUID)
          );
          this.answerTracker.set(
            this._uuidLookup.get(pageUUID) as Page,
            options as Option[]
          );
        }

        const visitedPageTrail = visitedPageTrailUUIDArray
          .map((UUID: string) => this._uuidLookup.get(UUID))
          .filter((element: any) => element !== undefined);
        this._flowChartSequence = flowChartSequence
          .map((UUID: string) => this._uuidLookup.get(UUID))
          .filter((element: any) => element !== undefined);

        // const previousPage = this._uuidLookup.get(previousPageUUID);
        const currentPage = this._uuidLookup.get(currentPageUUID);
        const nextPage = this._uuidLookup.get(nextPageUUID);
        this.props.setModels(loadSession.session.props.models);
        // this.props.setTools(tools);
        const newState = {
          complete,
          previousPage: null,
          currentPage: currentPage ?? this._flowChartSequence[0],
          nextPage: nextPage ?? visitedPageTrail[1] ?? null,
          template,
          sortedSpecsByWeight,
          tools,
          // showNothing: true,
          visitedPageTrail,
          isExisting: !complete, // in-progress sessions are existing sessions to be updated
          ULID: loadSession.ULID,
          sessionName: loadSession.session.name
        } as State;
        this.setState(newState as any);
        this._updateRankings(true);
        setTimeout(() => {
          this.setState({
            showNothing: false
          });
        }, 1000);
      }
    });
  }

  render() {
    const {
      previousPage,
      currentPage,
      nextPage,
      visitedPageTrail,
      rankings,
      isInputValid,
      optionToShowGlossary,
      isExisting,
      currentMobilePage,
      showGlossaryPopup
    } = this.state;
    if (this.state.showNothing) {
      return null;
    }

    return (
      <div className="selection-wizard">
        <div
          className="selection-wizard__column"
          style={{
            width: currentPage === MarkerPage.EXIT ? "100%" : null
          }}
        >
          <div className={`selection-wizard__${this.props.isMobile ? "column" : "row"}`}>
            <TransitionTrail
              visitedPageTrail={visitedPageTrail}
              currentPage={currentPage}
              currentMobilePage={currentMobilePage}
              onMakePageCurrent={this.onMakePageCurrent}
              isMobile={this.props.isMobile}
            />
            {currentPage !== MarkerPage.EXIT ? (
              <UserInputCollector
                currentPage={currentPage}
                answerTracker={this.answerTracker}
                onAnswerChange={this.onAnswerChange}
                onShowGlossary={this.onShowGlossary}
                isMobile={this.props.isMobile}
                toggleRightDrawerOpen={this.handlerToggleRightDrawerOpen}
                rightDrawerOpen={this.state.openRightDrawer}
              />
            ) : (
              <EvaluationResult
                rankings={rankings}
                modelComparisonTableRowSpecifications={
                  this.state.sortedSpecsByWeight
                }
                template={this.state.template}
                tools={this.state.tools}
              />
            )}
          </div>
          <WizardControl
            previousPage={previousPage}
            currentPage={currentPage}
            nextPage={nextPage}
            onGoToPage={this.onMakePageCurrent}
            onStartOver={this.onStartOver}
            onSaveSession={
              isExisting ? this.updateExistingSession : this.onSaveSession
            }
            isExisting={isExisting}
            name={this.state.sessionName}
            onComplete={() => {
              this.setState({
                ...this.state,
                complete: true
              });
            }}
            isMobile={this.props.isMobile}
          />
        </div>
        {currentPage !== MarkerPage.EXIT && (
          <Drawer
            placement="right"
            className={`selection-wizard__column ${!this.state.openRightDrawer ? "hidden" : ""}`}
            collapsed={window.innerWidth < 640}
            isMobile={this.props.isMobile}
            openRightDrawer={this.state.openRightDrawer}
            toggleOpenRightDrawer={() => this.setState({ openRightDrawer: !this.state.openRightDrawer })}
          >
            {isInputValid ? (
              <CurrentRanking rankings={rankings} />
            ) : (
              <InvalidInputIndicator />
            )}
            {!this.props.isMobile && <Glossary item={optionToShowGlossary} />}
          </Drawer>
        )}
        {showGlossaryPopup && this.props.isMobile && (
          <div
            className="selection-wizard__glossary-popup"
            onClick={() => this.setState({ showGlossaryPopup: false })}
          >
            <div
              className="selection-wizard__glossary-popup-content"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="selection-wizard__glossary-popup-close"
                onClick={() => this.setState({ showGlossaryPopup: false })}
              >
                <span className="material-icons">close</span>
              </button>
              {console.info({ optionToShowGlossary })}
              <Glossary item={optionToShowGlossary} />
            </div>
          </div>
        )}
      </div>
    );
  }

  async onSaveSession(name: string) {
    const session = await fetchAuthSession();
    const token = session.tokens?.accessToken?.toString() || '';
    fetch("/api/session/createSession", {
      method: "POST",
      body: JSON.stringify({
        accessToken: token,
        session: {
          props: {
            models: this.props.models,
            template: this.props.template,
            tools: this.props.tools
          },
          state: this.handleSerializingState(),
          internal: {
            answerTracker: this.serializeAnswerTracker(),
            pagesToVisit: this.handleSerializingQueue(),
            flowChartSequence: this.handleSerializingFlowSequence()
          },
          lastPageLabel: this.state.currentPage.label,
          name,
          complete: this.state.complete
        }
      })
    })
      .then((resp) => resp.json())
      .then((data) => data);

    Notification.open(`Session ${name ? `"${name}" ` : ''}saved.`);
  }

  handlerToggleRightDrawerOpen = () => {
    this.setState({ openRightDrawer: !this.state.openRightDrawer });
  };

  handleSerializingFlowSequence = () => {
    return this._flowChartSequence.map((value) => value.UUID);
  };

  handleSerializingState = () => {
    const { previousPage, currentPage, nextPage, visitedPageTrail, complete } =
      this.state;
    return {
      ...this.state,
      previousPage: previousPage?.UUID,
      currentPage: currentPage?.UUID,
      nextPage: nextPage?.UUID,
      visitedPageTrail: this.convertArrayOfObjectsToUUIDs(visitedPageTrail),
      complete
    };
  };

  handleSerializingQueue = () => {
    const results = [];
    let node = this._pagesToVisit.popBack();
    while (node) {
      results.push(node.UUID);
      node = this._pagesToVisit.popBack();
    }
    return results;
  };

  convertArrayOfObjectsToUUIDs = (array: any[]): string[] => {
    return array.map((item: any) => item.UUID);
  };

  // convertMapToObj(map: Map<Page, Option[]>) {
  serializeAnswerTracker() {
    const result = [...this.answerTracker].map(([key, value]) => {
      // go through next and change to uuids.\
      // for (const option of value) {
      //   option.next = option.next.map((nextItem: any) => nextItem.UUID);
      // }
      const options = value.map((option) => option.UUID);
      return [key.UUID, options];
    });
    return result;
  }

  convertAnswerTrackerObjToMap(answerTrackerArray: [string, any][]) {
    const lookup: Map<string, Page> = new Map();
    for (const [, definitionItem] of this._pageDefinitionMap) {
      lookup.set(definitionItem.UUID, definitionItem);
    }
    return new Map(
      answerTrackerArray.map(([localUUID, value]) => {
        for (const option of value) {
          option.next = option.next.map((UUID: string) => lookup.get(UUID));
        }
        return [lookup.get(localUUID), value];
      })
    );
  }

  updateExistingSession = async (name: string) => {
    const session = await fetchAuthSession();
    const token = session.tokens?.accessToken?.toString() || '';

    await fetch("/api/session/updateSession", {
      method: "POST",
      body: JSON.stringify({
        accessToken: token,
        sessionULID: this.state.ULID,
        session: {
          props: {
            models: this.props.models,
            template: this.props.template,
            tools: this.props.tools
          },
          state: this.handleSerializingState(),
          internal: {
            answerTracker: this.serializeAnswerTracker(),
            pagesToVisit: this.handleSerializingQueue(),
            flowChartSequence: this.handleSerializingFlowSequence()
          },
          lastPageLabel: this.state.currentPage.label,
          name: name ? name : this.state.sessionName,
          complete: this.state.complete
        }
      })
    })
      .then((resp) => resp.json())
      .then((data) => data);

    Notification.open(
      `Session "${name ? name : this.state.sessionName}" updated.`
    );
  };

  onMakePageCurrent(newPage: Page) {
    let doesHavePageWithMissingAnswer = false;
    if (newPage.level === 0) {
      this.setState({ currentMobilePage: newPage });
    }

    if (this._isPageAfterCurrentPage(newPage)) {
      /**
       * When we selected a forward page, we want to make sure that the current page
       * has a valid answer before letting the user proceed.
       */
      if (!this._doesPageHaveValidAnswer(this.state.currentPage)) {
        doesHavePageWithMissingAnswer = true;
        Notification.open(
          `Please select ${this.state.currentPage.multiple ? "at least" : ""
          } one answer before proceeding.`
        );
      } else {
        /**
         * We want to check if, between the current page and the new page, there is any
         * page that is required but still does not have a selected answer, in that case,
         * we want to prevent the user from going to that new page.
         */
        const visitedPageTrail = this.state.visitedPageTrail;
        const indexOfPageAfterCurrentPage =
          visitedPageTrail.indexOf(this.state.currentPage) + 1;
        const indexOfNewPage = visitedPageTrail.indexOf(newPage);
        for (let i = indexOfPageAfterCurrentPage; i < indexOfNewPage; i++) {
          const currentSkippedPage = visitedPageTrail[i];
          if (!this._doesPageHaveValidAnswer(currentSkippedPage)) {
            const errorMessages = [
              `Page **${currentSkippedPage.label}** is required, but it has no selected answer.`,
              `Please select ${currentSkippedPage.multiple ? "**at least**" : ""
              } **one** answer on that page before proceeding.`
            ];
            Notification.open(<Markdown>{errorMessages.join("\n")}</Markdown>);
            doesHavePageWithMissingAnswer = true;
            break;
          }
        }
      }
    }
    if (!doesHavePageWithMissingAnswer) {
      this._goToNewPage(newPage);
    }
  }

  private _isPageAfterCurrentPage(newPage: Page) {
    const indexOfNewPage = this.state.visitedPageTrail.indexOf(newPage);
    return indexOfNewPage === -1
      ? true
      : indexOfNewPage >
      this.state.visitedPageTrail.indexOf(this.state.currentPage);
  }

  private _doesPageHaveValidAnswer(page: Page) {
    return !page.required ? true : this.answerTracker.get(page)?.length > 0;
  }

  private _goToNewPage(newPage: Page) {
    if (newPage !== MarkerPage.EXIT) {
      if (!this.answerTracker.has(this.state.currentPage)) {
        this.answerTracker.set(this.state.currentPage, []);
      }
      this._updatePagesToVisit(newPage);
    } else {
      this._pagesToVisit.clear();
    }
    this._updateRankings(newPage !== MarkerPage.EXIT);
    this._updatePageNavigation(newPage);
    this.setState({
      optionToShowGlossary: null
    });
  }

  private _updatePagesToVisit(newPage: Page) {
    this._pagesToVisit.popFront();
    const shouldAddToTheFront = this._pagesToVisit.count() > 0;
    const currentAnswer = this.answerTracker.get(this.state.currentPage);
    let pageNumber = 1;
    for (const option of currentAnswer) {
      for (const nextPage of shouldAddToTheFront
        ? option.next.reverse()
        : option.next) {
        if (!this._pagesToVisit.has(nextPage)) {
          this._pagesToVisit[shouldAddToTheFront ? "addFront" : "addBack"](
            nextPage
          );
          nextPage.number = pageNumber;
          pageNumber++;
        }
      }
    }
    if (!this._pagesToVisit.has(newPage)) {
      this._pagesToVisit.addFront(newPage);
    }
  }

  private _updateRankings(ignoreRequired: boolean) {
    const { template, tools } = this.props;
    const input = this._buildInputObject();
    if (isValidInput(input, template, ignoreRequired)) {
      const weightedInput = getWeights(input, template, 10);
      const scoringResults = Object.entries(tools)
        .map(([toolName, toolCapability]) => {
          const modelForCurrentTool = this.props.models.find((model) => {
            return model.acronym.includes(toolName);
          });
          if (modelForCurrentTool) {
            return {
              model: modelForCurrentTool,
              result: getScore(weightedInput, toolCapability)
            };
          }
          return null;
        })
        .filter((result) => result !== null)
        /*
         * Sort by scores in descending order, then sort by names in alphabetical order
         */
        .sort(
          (a, b) =>
            b.result.score - a.result.score ||
            a.model.acronym.localeCompare(b.model.acronym)
        );
      const sortedSpecsForAllTools = sortSpecs4AllTools(
        scoringResults.reduce((accumulator, result) => {
          accumulator[result.model.acronym] = result.result;
          return accumulator;
        }, {} as any)
      );

      this.setState({
        isInputValid: true,
        rankings: scoringResults.map((result) => ({
          model: result.model,
          score: Math.round(result.result.score * 10) / 10,
          reasonings: this._deriveReasoningListForModel(
            template,
            sortedSpecsForAllTools.find((e) => e.tool === result.model.acronym)
              .sortedSpecs
          )
        }))
      });
      if (!ignoreRequired) {
        this.setState({
          sortedSpecsByWeight: sortSpecsByWeight(weightedInput).filter(
            (spec) => spec.weight > 0
          ),
          tools,
          template
        });
      }
    } else {
      console.warn("Invalid input:", input);
      this.setState({
        isInputValid: false,
        nextPage: null
      });
    }
  }

  private _buildInputObject() {
    const topLevelPages = this.state.visitedPageTrail.reduce(
      (accumulator, visitedPage) => {
        if (visitedPage.level === 0 && visitedPage !== MarkerPage.EXIT) {
          accumulator.push(visitedPage);
        }
        return accumulator;
      },
      [] as Page[]
    );
    return topLevelPages.reduce((accumulator, topLevelPage) => {
      const inputForTopLevelPage = this._gatherInputForPage(topLevelPage);
      if (topLevelPage.key) {
        accumulator[topLevelPage.key] = inputForTopLevelPage;
      } else {
        accumulator = Object.assign(accumulator, inputForTopLevelPage);
      }
      return accumulator;
    }, {} as any);
  }

  private _gatherInputForPage(page: Page) {
    const answer = this.answerTracker.get(page);
    if (answer) {
      return this.answerTracker.get(page).reduce((result, option) => {
        for (const action of option.actions) {
          for (const actionKey of Object.keys(action)) {
            const keyPath = actionKey.split("/");
            let actionBase = result as any;
            for (const subkey of keyPath.slice(0, -1)) {
              actionBase = actionBase[subkey] = actionBase[subkey] || {};
            }
            actionBase[keyPath[keyPath.length - 1]] = action[actionKey];
          }
        }
        for (const nextPage of option.next) {
          const input = this._gatherInputForPage(nextPage);
          if (nextPage.key) {
            let nextPageResult = result as any;
            for (const subkey of nextPage.key.split("/")) {
              nextPageResult = nextPageResult[subkey] =
                nextPageResult[subkey] || {};
            }
            Object.assign(nextPageResult, input);
          } else {
            result = Object.assign(result, input);
          }
        }
        return result;
      }, {});
    }
    return {};
  }

  /**
   * For each element (e.g. "ESS_tech/battery/nonlinear_DOR") in `sortedSpecsForModel`,
   * we want to split them up on forward slashes, each forward slash represents a layer name
   * (e.g. ESS_tech, battery, or nonlinear_DOR) which is then used to look up the corresponding
   * object inside `template.json` recursively and the list of layer names is exhausted, then
   * we return the layer label to be used as the reasoning item.
   *
   * @param template The `template.json` file imported from `msp-scoring` module
   * @param sortedSpecsForModel Each element in the `sortedSpecsForModel` looks like this "a/b/c/or/whatever"
   * @returns
   */
  private _deriveReasoningListForModel(
    template: Template,
    sortedSpecsForModel: Array<{ path: string; score: number }>
  ) {
    return sortedSpecsForModel
      .filter((spec) => spec.score > 0)
      .slice(0, 3)
      .map((spec) => {
        let layer: TemplateLayer | TemplateLayerChildren = null;
        for (const layerName of spec.path.split("/")) {
          if (layer === null) {
            layer = template[layerName];
          } else if ("children" in layer) {
            layer = (layer as TemplateLayer).children[layerName];
          } else {
            layer = layer[layerName as keyof TemplateLayerChildren] as (TemplateLayerChildren);
          }
        }
        return layer?.label;
      });
  }

  private _updatePageNavigation(newPage: Page) {
    const visitedPageTrail = this.state.visitedPageTrail;
    const newPageIndex = visitedPageTrail.indexOf(newPage);
    this.setState({
      previousPage: visitedPageTrail[newPageIndex - 1],
      currentPage: newPage,
      nextPage: this._doesPageHaveValidAnswer(newPage)
        ? visitedPageTrail[newPageIndex + 1]
        : null
    });
  }

  onAnswerChange(newAnswer: Option[]) {
    const currentPage = this.state.currentPage;
    const previousAnswer = this.answerTracker.get(currentPage) || [];
    if (!this._answersEqual(newAnswer, previousAnswer)) {
      this.answerTracker.set(currentPage, newAnswer);
      this._updateVisitedPageTrailOnAnswerChange(
        previousAnswer,
        newAnswer
      ).then(() => {
        this.setState({
          nextPage: this._doesPageHaveValidAnswer(currentPage)
            ? this._getPageAfterCurrentPage()
            : null
        });
      });
      this._updateRankings(currentPage !== MarkerPage.EXIT);
    }
  }

  private _answersEqual(answer1: Option[], answer2: Option[]) {
    if (answer1.length !== answer2.length) {
      return false;
    }
    for (let i = 0; i < answer1.length; i++) {
      if (answer1[i] !== answer2[i]) {
        return false;
      }
    }
    return true;
  }

  private _updateVisitedPageTrailOnAnswerChange(
    previousAnswer: Option[],
    newAnswer: Option[]
  ) {
    return new Promise<void>((resolve) => {
      const visitedPageTrail = this.state.visitedPageTrail;
      const diffResult = this._calculateAddedAndRemovedPages(
        previousAnswer,
        newAnswer
      );
      const childPagesOfPagesToRemove = diffResult.removedPages.reduce(
        (accumulator, pageToRemove) => {
          const indexOfPageToRemove = visitedPageTrail.indexOf(pageToRemove);
          for (const childPage of visitedPageTrail.slice(
            indexOfPageToRemove + 1
          )) {
            if (childPage.level <= pageToRemove.level) {
              break;
            }
            accumulator.push(childPage);
          }
          return accumulator;
        },
        [] as Page[]
      );
      const pagesReferencedInNewAnswer = newAnswer.reduce(
        (accumulator, option) => {
          accumulator.push(...option.next);
          return accumulator;
        },
        [] as Page[]
      );
      const updatedVisitedPageTrail =
        diffResult.removedPages.length === 0
          ? visitedPageTrail
          : visitedPageTrail.filter((visitedPage) => {
            if (
              /*
               * 1. If `visitedPage` is the one to be removed AND it's not being referenced by the other options
               *    in the new answer, then we remove it
               * 2. If `visitedPage` is a child of the page to be removed, then we remove it
               */
              (diffResult.removedPages.includes(visitedPage) &&
                !pagesReferencedInNewAnswer.includes(visitedPage)) ||
              childPagesOfPagesToRemove.includes(visitedPage)
            ) {
              this.answerTracker.delete(visitedPage);
              return false;
            }
            return true;
          });
      if (diffResult.addedPages.length > 0) {
        const indexOfCurrentPage = updatedVisitedPageTrail.indexOf(
          this.state.currentPage
        );
        /*
         * We are looking for those pages that are not already present in the visited page trail
         */
        const newPages = diffResult.addedPages.filter(
          (addedPage) => !updatedVisitedPageTrail.includes(addedPage)
        );
        updatedVisitedPageTrail.splice(indexOfCurrentPage + 1, 0, ...newPages);
        for (const newPage of newPages) {
          this.answerTracker.set(newPage, []);
        }
      }
      this.setState(
        {
          visitedPageTrail: updatedVisitedPageTrail
        },
        resolve
      );
    });
  }

  private _calculateAddedAndRemovedPages(
    previousAnswer: Option[],
    newAnswer: Option[]
  ) {
    const result = {
      addedPages: [] as Page[],
      removedPages: [] as Page[]
    };

    for (const optionInPreviousAnswer of previousAnswer) {
      if (!newAnswer.includes(optionInPreviousAnswer)) {
        result.removedPages.push(...optionInPreviousAnswer.next);
      }
    }
    for (const optionInNewAnswer of newAnswer) {
      if (!previousAnswer.includes(optionInNewAnswer)) {
        result.addedPages.push(...optionInNewAnswer.next);
      }
    }

    return result;
  }

  private _getPageAfterCurrentPage() {
    const indexOfCurrentPage = this.state.visitedPageTrail.indexOf(
      this.state.currentPage
    );
    return this.state.visitedPageTrail[indexOfCurrentPage + 1];
  }

  onShowGlossary(item: Option) {
    console.info("onShowGlossary", item);
    if (this.props.isMobile) {
      this.setState({
        optionToShowGlossary: item,
        showGlossaryPopup: true,
        openRightDrawer: false
      });
    } else {
      this.setState({
        optionToShowGlossary: item,
        openRightDrawer: true
      });
    }
  }

  onStartOver() {
    ConfirmationDialog.open("Are you sure you want to start over?").then(() => {
      this.answerTracker.clear();
      this._pagesToVisit.clear();
      this.setState({
        previousPage: null,
        currentPage: this._flowChartSequence[0],
        nextPage: null,
        visitedPageTrail: [...this._flowChartSequence],
        rankings: [],
        optionToShowGlossary: null,
        isInputValid: true
      });
    });
  }
}
